import { useMemo } from 'react'
import { Currency, CurrencyAmount } from '@pancakeswap-libs/sdk'
import { BigNumber } from '@ethersproject/bignumber'
import { AggregatedQuoteResultBest } from '../aggregator'
import { useTransactionAdder } from '../state/transactions/hooks'
import { useActiveWeb3React } from './index'

export enum AggregatorSwapState {
  INVALID,
  LOADING,
  VALID,
}

interface AggregatorSwapCallback {
  state: AggregatorSwapState
  callback: null | (() => Promise<string>)
  error: string | null
}

/** Buffer on the aggregator's gas estimate. Routed swaps touch several pools and a
 *  tight estimate reverts when pool state moves between quote and execution. */
const GAS_LIMIT_BUFFER_PERCENT = 20

function withBuffer(gas: string | undefined): BigNumber | undefined {
  if (!gas) return undefined
  try {
    return BigNumber.from(gas).mul(100 + GAS_LIMIT_BUFFER_PERCENT).div(100)
  } catch {
    return undefined
  }
}

function describeSwapError(error: unknown): string {
  const code = typeof error === 'object' && error !== null ? (error as { code?: number }).code : undefined
  if (code === 4001) return 'Transaction rejected in your wallet.'

  const message =
    typeof error === 'object' && error !== null
      ? String((error as { message?: unknown }).message ?? '')
      : ''

  if (/insufficient funds/i.test(message)) {
    return 'Not enough BNB to cover the transaction and gas.'
  }
  if (/slippage|INSUFFICIENT_OUTPUT|min.*return/i.test(message)) {
    return 'Price moved beyond your slippage tolerance. Try again or raise the tolerance.'
  }
  return message || 'The swap failed. Please try again.'
}

/**
 * Executes a swap using the aggregator's own calldata.
 *
 * Unlike the on-chain router path, nothing here builds a transaction: the quote
 * carries `to`, `data` and `value` already, and altering any of them would break the
 * route the quote was priced against. The wallet signs exactly what was quoted.
 */
export default function useAggregatorSwapCallback(
  quote: AggregatedQuoteResultBest | null,
  inputAmount: CurrencyAmount | undefined,
  inputCurrency: Currency | undefined,
  outputCurrency: Currency | undefined
): AggregatorSwapCallback {
  const { account, chainId, library } = useActiveWeb3React()
  const addTransaction = useTransactionAdder()

  return useMemo(() => {
    if (!library || !account || !chainId) {
      return { state: AggregatorSwapState.INVALID, callback: null, error: 'Connect your wallet' }
    }
    if (!quote) {
      return { state: AggregatorSwapState.LOADING, callback: null, error: null }
    }
    if (!quote.transaction) {
      // An indicative quote with no calldata - normally means the taker address was
      // missing when it was fetched.
      return { state: AggregatorSwapState.INVALID, callback: null, error: 'Quote is not executable' }
    }

    const { transaction } = quote

    return {
      state: AggregatorSwapState.VALID,
      callback: async function onAggregatorSwap(): Promise<string> {
        const signer = library.getSigner()

        const request = {
          to: transaction.to,
          data: transaction.data,
          value: BigNumber.from(transaction.value || '0'),
          gasLimit: withBuffer(transaction.gas),
        }

        let response
        try {
          response = await signer.sendTransaction(request)
        } catch (error) {
          throw new Error(describeSwapError(error))
        }

        const inputSymbol = inputCurrency?.symbol ?? ''
        const outputSymbol = outputCurrency?.symbol ?? ''
        const inputText = inputAmount?.toSignificant(4) ?? ''

        addTransaction(response, {
          summary: `Swap ${inputText} ${inputSymbol} for ${outputSymbol} via ${quote.providerLabel}`,
        })

        return response.hash
      },
      error: null,
    }
  }, [library, account, chainId, quote, inputAmount, inputCurrency, outputCurrency, addTransaction])
}
